import { CutlistOrder } from '@cutr/constants/cutlist';
import {
  BackendCutlistGroup,
  BackendPart,
} from '@cutr/constants/cutlist-backend';
import {
  CUTLIST_LLM_SUPPORTED_EXTENSIONS,
  FileUpload,
} from '@cutr/constants/cutlist-llm.ts';
import { nanoid } from 'nanoid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { api, ApiError } from '@/api/backend';
import { liveUpdates } from '@/api/liveUpdates';
import {
  generateMaterialGroup,
  useMaterialGroupState,
} from '@/api/materialsGroup';
import { useActions, useCutlistState } from '@/api/store';
import { generatePartItem } from '@/api/store';
import { InfoMessage } from '@/blocks/InfoMessage';
import { Button } from '@/primitives/Button';
import {
  CheckmarkCircleFull,
  CircledX,
  Clock,
  Icon,
  Info,
  Loader,
  Trash,
  Upload,
} from '@/primitives/Icons';
import { useCurrentFeatures } from '@/theme';
import { getTrimConfigByType } from '@/utils/features';

import styles from './LlmFileImport.module.css';

const ANALYZING_TIMEOUT = 60_000;

type ValidationError = {
  severity: 'critical' | 'error' | 'warning';
  code: string;
  timestamp: string;
  info: string;
  fields: string[];
};

type UploadStatus = 'uploading' | 'done' | 'error';

type UploadedFile = FileUpload & {
  id: string;
  status: UploadStatus;
  progress: number;
  file?: File;
};

type CutlistUploadFileProcessedEvent = {
  cutlistId: CutlistOrder['id'];
  cutlistGroups?: BackendCutlistGroup[];
  errors?: ValidationError[];
};

type AnalysisStatus =
  | 'idle'
  | 'analyzing'
  | 'success'
  | 'error'
  | 'noResults'
  | 'timedOut';

export const LlmFileImport = () => {
  const { t } = useTranslation();
  const cutlistId = useCutlistState((state) => state.orderId);
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [isTriggeringLlmFlow, setIsTriggeringLlmFlow] = React.useState(false);
  const isUploadingFiles = uploadedFiles.some((f) => f.status === 'uploading');
  const [analysisStatus, setAnalysisStatus] =
    React.useState<AnalysisStatus>('idle');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errors, setErrors] = React.useState<ValidationError[]>([]);
  const { setPart } = useActions();
  const { addGroup } = useMaterialGroupState();
  const { supportedSheetEdgeTrims, defaultSheetEdgeTrimTypeForPartGroup } =
    useCurrentFeatures();

  React.useEffect(() => {
    if (!cutlistId) return;
    const unsubscribe =
      liveUpdates.handleEvent<CutlistUploadFileProcessedEvent>(
        'cutlist_upload_file_processed',
        (data) => {
          if (data.payload.cutlistId !== cutlistId) return;

          if (data.payload.errors) {
            setErrors(data.payload.errors);
            console.error('Errors in live update', data.payload.errors);
            setAnalysisStatus('error');
            toast.error(t('order-file-upload.analysisError'));
            return;
          }
          const cutlistGroups = data.payload.cutlistGroups;
          if (!cutlistGroups) return;

          if (cutlistGroups.length > 0) {
            cutlistGroups.forEach((group) => {
              const materialGroupData = group.materialGroup;
              const parts = group.parts;
              const materialGroup = generateMaterialGroup({
                ...materialGroupData,
                id: materialGroupData.groupId,
                type: 'panels-and-strips',
                sheetEdgeTrimConfig: getTrimConfigByType(
                  supportedSheetEdgeTrims,
                  defaultSheetEdgeTrimTypeForPartGroup
                ),
              });
              addGroup(materialGroup);
              parts.forEach((part: BackendPart) => {
                setPart(
                  generatePartItem({
                    ...part,
                    groupId: materialGroup.id,
                  })
                );
              });
            });
            toast.success(t('order-file-upload.analysisSuccess'));
            setAnalysisStatus('success');
          }

          if (cutlistGroups.length === 0) {
            toast.info(t('order-file-upload.analysisNoResults'));
            setAnalysisStatus('noResults');
          }
        }
      );
    return unsubscribe;
  }, [
    cutlistId,
    addGroup,
    setPart,
    t,
    supportedSheetEdgeTrims,
    defaultSheetEdgeTrimTypeForPartGroup,
  ]);

  React.useEffect(() => {
    if (analysisStatus !== 'analyzing') return;

    const timeout = setTimeout(() => {
      setAnalysisStatus('timedOut');
      toast.error(t('order-file-upload.analysisTimeout'));
    }, ANALYZING_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [analysisStatus, t]);

  React.useEffect(() => {
    if (!cutlistId) return;
    const controller = new AbortController();
    api
      .getCutlistFileImport(cutlistId, controller.signal)
      .then((data) => {
        setUploadedFiles(
          (data?.files ?? []).map((f) => ({
            ...f,
            id: nanoid(),
            status: 'done',
            progress: 100,
          }))
        );
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        throw err;
      });
    return () => {
      controller.abort();
    };
  }, [cutlistId]);

  const updateFile = (id: string, patch: Partial<UploadedFile>) => {
    setUploadedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f))
    );
  };

  const startUpload = (file: File) => {
    const id = nanoid();
    const entry: UploadedFile = {
      id,
      uri: '',
      name: file.name,
      size: file.size,
      mimetype: file.type,
      status: 'uploading',
      progress: 0,
      file,
    };

    setUploadedFiles((prev) => [...prev, entry]);

    api
      .uploadFileImport(file, cutlistId!, (progress) =>
        updateFile(id, { progress })
      )
      .then(({ uri }) => {
        updateFile(id, { status: 'done', progress: 100, uri });
      })
      .catch(() => updateFile(id, { status: 'error' }));
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(startUpload);
  };

  const removeFile = async (item: UploadedFile) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== item.id));
    if (item.uri && cutlistId) {
      await api.deleteCutlistFileImportFile(cutlistId, item.uri).catch(() => {
        // Restore the file in local state if the server delete fails
        setUploadedFiles((prev) => [...prev, item]);
      });
    }
  };

  const handleTriggerLlmFlow = async () => {
    setIsTriggeringLlmFlow(true);
    setAnalysisStatus('analyzing');
    await api
      .triggerLlmFlow(cutlistId!)
      .catch((err) => {
        setAnalysisStatus('error');
        if (err instanceof ApiError && err.response.status === 422) {
          toast.error(err.message);
        }
      })
      .finally(() => setIsTriggeringLlmFlow(false));
  };

  const retryUpload = (item: UploadedFile) => {
    if (!item.id) return;
    updateFile(item.id, { status: 'uploading', progress: 0 });

    api
      .uploadFileImport(item.file!, cutlistId!, (progress) =>
        updateFile(item.id!, { progress })
      )
      .then(({ uri }) =>
        updateFile(item.id!, { status: 'done', progress: 100, uri })
      )
      .catch(() => updateFile(item.id!, { status: 'error' }));
  };

  const StatusIndicator = () => {
    const iconSize = 3;
    if (analysisStatus === 'analyzing') {
      return (
        <>
          <Icon icon={<Loader />} size={iconSize} />
          <span>{t('order-file-upload.analyzingDescription')}</span>
        </>
      );
    }

    if (analysisStatus === 'success') {
      return (
        <>
          <Icon
            icon={<CheckmarkCircleFull />}
            size={iconSize}
            color="var(--green-5)"
          />
          <span>{t('order-file-upload.analysisSuccess')}</span>
        </>
      );
    }

    if (analysisStatus === 'noResults') {
      return (
        <>
          <Icon icon={<Info />} size={iconSize} />
          <span>{t('order-file-upload.analysisNoResults')}</span>
        </>
      );
    }

    if (analysisStatus === 'error') {
      return (
        <>
          <Icon
            icon={<CircledX />}
            size={iconSize}
            color="var(--color-alert)"
          />
          <span>{t('order-file-upload.analysisError')}</span>
        </>
      );
    }

    if (analysisStatus === 'timedOut') {
      return (
        <>
          <Icon icon={<Clock />} size={iconSize} />
          <span>{t('order-file-upload.analysisTimeout')}</span>
        </>
      );
    }

    return null;
  };

  return (
    <div>
      <h2 className={styles.header}>{t('order-file-upload.header')}</h2>
      <label
        className={`${styles.uploadArea} ${
          isDragOver ? styles.uploadAreaDragOver : ''
        }`}
        htmlFor="file-upload"
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <Icon icon={<Upload />} size={8} className={styles.uploadIcon} />
        <span className={styles.uploadText}>
          <strong>{t('order-file-upload.uploadCta')}</strong>
        </span>
        <span className={styles.uploadFormats}>
          {t('order-file-upload.uploadFormats', {
            formats: CUTLIST_LLM_SUPPORTED_EXTENSIONS.join(', '),
          })}
        </span>
        <input
          id="file-upload"
          type="file"
          multiple
          className={styles.hiddenInput}
          accept={CUTLIST_LLM_SUPPORTED_EXTENSIONS.join(',')}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </label>

      {uploadedFiles.length > 0 && (
        <ul className={styles.fileList}>
          {uploadedFiles.map((item) => (
            <FileRow
              key={item.id}
              item={item}
              retryUpload={() => retryUpload(item)}
              removeFile={removeFile}
            />
          ))}
        </ul>
      )}
      {uploadedFiles.some((f) => f.status === 'done') && (
        <div className={styles.triggerLlmFlowContainer}>
          <Button
            onClick={handleTriggerLlmFlow}
            disabled={
              isTriggeringLlmFlow ||
              isUploadingFiles ||
              analysisStatus === 'analyzing'
            }
            style={{ width: 'fit-content' }}
          >
            {t('order-file-upload.triggerLlmFlow')}
          </Button>
          {analysisStatus !== 'idle' && (
            <div className="flexAlign gap-xs">
              <StatusIndicator />
            </div>
          )}
        </div>
      )}
      {analysisStatus !== 'idle' && (
        <InfoMessage
          message={t('order-file-upload.analysisInfoDescription')}
          variant="ai"
        />
      )}
    </div>
  );
};

interface FileRowProps {
  item: UploadedFile;
  retryUpload: () => void;
  removeFile: (item: UploadedFile) => void;
}

const FileRow = ({ item, retryUpload, removeFile }: FileRowProps) => {
  const { t } = useTranslation();
  return (
    <li key={item.id} className={styles.fileRow}>
      <div className={styles.fileRowHeader}>
        <Icon
          icon={
            item.status === 'uploading' ? (
              <Loader />
            ) : item.status === 'done' ? (
              <CheckmarkCircleFull />
            ) : (
              <CircledX />
            )
          }
          size={4}
          className={
            item.status === 'done'
              ? styles.iconSuccess
              : item.status === 'error'
                ? styles.iconError
                : styles.iconUploading
          }
        />
        <div className={styles.fileName}>
          <strong>{item.name}</strong>
          {item.status === 'uploading' && (
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${item.progress}%` }}
              />
            </div>
          )}
        </div>
        <div className={styles.fileRowActions}>
          {item.status === 'error' && item.file && (
            <Button variant="link" size="xs" onClick={retryUpload}>
              {t('order-file-upload.retry')}
            </Button>
          )}
          <Button
            variant="icon"
            className="delete"
            icon={<Icon icon={<Trash />} />}
            tooltip={t('order-file-upload.removeFile')}
            onClick={() => removeFile(item)}
          />
        </div>
      </div>
    </li>
  );
};
